import { Pencil, Trash2 } from "lucide-react";
import React from "react";
import { Button } from "../../../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../../components/ui/common/tooltip";

interface RecordStripProps {
  /** Zero-based position in the list — rendered as `idx + 1`. */
  idx: number;
  title: string;
  /** Small pill beside the title (e.g. the record's type label). */
  badge?: React.ReactNode;
  /** Right-aligned value, typically mono-spaced (e.g. `120 kg`). */
  right?: React.ReactNode;
  /** Extra content after `right`, before the actions (e.g. a route-count badge). */
  trailing?: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

/**
 * The compact read strip shared by every `mappingReview` record list: index,
 * name, optional badge, right-aligned value, and a pencil/trash action pair.
 */
const RecordStrip: React.FC<RecordStripProps> = ({
  idx,
  title,
  badge,
  right,
  trailing,
  onEdit,
  onDelete,
  disabled = false
}) => (
  <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-3 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50">
    <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
      <span className="tw-font-semibold tw-text-muted-foreground tw-w-5">{idx + 1}</span>
      <span className="tw-font-medium tw-text-foreground">{title || "—"}</span>
      {badge}
      <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">{right}</span>
      {trailing}
    </div>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
            onClick={onEdit}
            disabled={disabled}
          >
            <Pencil className="tw-h-3 tw-w-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="tw-text-xs">Edit</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <Button
      variant="ghost"
      size="icon"
      className="tw-h-7 tw-w-7 tw-text-destructive"
      onClick={onDelete}
      disabled={disabled}
    >
      <Trash2 className="tw-h-3 tw-w-3" />
    </Button>
  </div>
);

export default RecordStrip;
